<template>
    <div>
        <div class="d-flex align-items-center justify-content-between mb-3">
            <h3 class="mb-0">Status follow-ups</h3>
            <el-button type="success" @click="openCreate">New rule</el-button>
        </div>

        <div class="card mb-3">
            <div class="card-body">
                <el-form label-position="top" @submit.prevent>
                    <el-row :gutter="16" align="bottom">
                        <el-col :span="8">
                            <el-form-item label="Meta session window (hours)" class="mb-0">
                                <el-input-number v-model="windowHours" :min="1" :max="168" controls-position="right" />
                            </el-form-item>
                        </el-col>
                        <el-col :span="16">
                            <p class="text-muted mb-2">Free text is sent only when the latest enquiry for this phone number was created inside this window. Outside it, text is skipped and the combination mode sends the template.</p>
                            <el-button type="primary" :loading="savingWindow" @click="saveWindow">Save window</el-button>
                            <span v-if="windowError" class="text-danger ml-2">{{ windowError }}</span>
                            <span v-if="windowSaved" class="text-success ml-2">Saved</span>
                        </el-col>
                    </el-row>
                </el-form>
            </div>
        </div>

        <el-alert
            v-if="templatesError"
            :title="templatesError"
            type="warning"
            :closable="false"
            show-icon
            class="mb-3"
        />
        <el-alert
            v-if="ruleError"
            :title="ruleError"
            type="error"
            show-icon
            :closable="false"
            class="mb-3"
        />

        <el-tabs v-model="tab">
            <el-tab-pane label="Rules" name="rules">
                <div class="card">
                    <div class="card-body">
                        <el-table :data="rules" v-loading="loading" empty-text="No status follow-up rules yet.">
                            <el-table-column prop="name" label="Name" min-width="150" />
                            <el-table-column label="When status is" min-width="260">
                                <template #default="{ row }">
                                    <div v-for="item in statusList(row)" :key="item.action_type + item.status_label" class="mb-1">
                                        <el-tag size="small">{{ item.action_label }} — {{ item.status_label }}</el-tag>
                                    </div>
                                </template>
                            </el-table-column>
                            <el-table-column label="Send" min-width="200">
                                <template #default="{ row }">
                                    <div>{{ timingLabel(row) }}</div>
                                    <div class="text-muted">{{ repeatLabel(row) }}</div>
                                </template>
                            </el-table-column>
                            <el-table-column label="Message" min-width="160">
                                <template #default="{ row }">
                                    {{ modeLabel(row.message_mode) }}
                                </template>
                            </el-table-column>
                            <el-table-column label="Status" width="220">
                                <template #default="{ row }">
                                    <el-switch
                                        :model-value="row.status"
                                        active-value="active"
                                        inactive-value="inactive"
                                        active-text="Active"
                                        inactive-text="Inactive"
                                        :loading="statusSavingId === row.id"
                                        :disabled="statusSavingId !== null && statusSavingId !== row.id"
                                        :before-change="() => confirmStatus(row)"
                                        @update:model-value="(value) => { row.status = value }"
                                    />
                                </template>
                            </el-table-column>
                            <el-table-column label="Sent / failed" width="120">
                                <template #default="{ row }">
                                    {{ row.sent_count }} / {{ row.failed_count }}
                                </template>
                            </el-table-column>
                            <el-table-column label="Actions" width="100" fixed="right">
                                <template #default="{ row }">
                                    <el-button size="small" @click="openEdit(row)">Edit</el-button>
                                </template>
                            </el-table-column>
                        </el-table>
                    </div>
                </div>
            </el-tab-pane>
            <el-tab-pane label="Logs" name="logs">
                <div class="card">
                    <div class="card-body">
                        <el-row :gutter="12" class="mb-3">
                            <el-col :span="8">
                                <el-select v-model="logFilters.rule_id" clearable placeholder="All rules" style="width: 100%" @change="loadLogs(1)">
                                    <el-option v-for="rule in rules" :key="rule.id" :label="rule.name" :value="rule.id" />
                                </el-select>
                            </el-col>
                            <el-col :span="6">
                                <el-select v-model="logFilters.status" clearable placeholder="All results" style="width: 100%" @change="loadLogs(1)">
                                    <el-option label="Success" value="success" />
                                    <el-option label="Failed" value="failed" />
                                    <el-option label="Skipped" value="skipped" />
                                    <el-option label="Waiting" value="pending" />
                                </el-select>
                            </el-col>
                        </el-row>
                        <el-table :data="logs" v-loading="logsLoading" empty-text="No follow-up logs yet.">
                            <el-table-column label="When" min-width="160">
                                <template #default="{ row }">
                                    {{ row.sent_at || row.scheduled_for || '—' }}
                                </template>
                            </el-table-column>
                            <el-table-column prop="rule_name" label="Rule" min-width="140" />
                            <el-table-column label="Enquiry" min-width="160">
                                <template #default="{ row }">
                                    <div>#{{ row.enquiry_id }} {{ row.enquiry_name }}</div>
                                    <div class="text-muted">{{ row.status_label }}</div>
                                </template>
                            </el-table-column>
                            <el-table-column label="Result" width="110">
                                <template #default="{ row }">
                                    <el-tag :type="resultType(row.status)">{{ resultLabel(row.status) }}</el-tag>
                                </template>
                            </el-table-column>
                            <el-table-column label="Channel" min-width="140">
                                <template #default="{ row }">
                                    <div>{{ channelLabel(row) }}</div>
                                    <div class="text-muted">{{ windowLabel(row) }}</div>
                                </template>
                            </el-table-column>
                            <el-table-column label="Message" min-width="220">
                                <template #default="{ row }">
                                    <div style="white-space: pre-wrap">{{ row.message_preview || '—' }}</div>
                                    <div v-if="row.error" class="text-danger">{{ row.error }}</div>
                                </template>
                            </el-table-column>
                        </el-table>
                        <div class="d-flex justify-content-end mt-3" v-if="logTotal > logFilters.per_page">
                            <el-pagination
                                background
                                layout="prev, pager, next"
                                :page-size="logFilters.per_page"
                                :total="logTotal"
                                :current-page="logFilters.page"
                                @current-change="loadLogs"
                            />
                        </div>
                        <p v-if="logsError" class="text-danger mt-2 mb-0">{{ logsError }}</p>
                    </div>
                </div>
            </el-tab-pane>
        </el-tabs>

        <el-dialog v-model="dialog" :title="form.id ? 'Edit rule' : 'New rule'" width="760px" top="4vh">
            <el-form label-position="top" @submit.prevent>
                <el-alert
                    v-if="formError"
                    :title="formError"
                    type="error"
                    show-icon
                    :closable="false"
                    class="mb-3"
                />
                <el-form-item label="Rule name" required>
                    <el-input v-model="form.name" maxlength="191" placeholder="Call not picked" />
                </el-form-item>
                <el-row :gutter="16">
                    <el-col :span="12">
                        <el-form-item label="Rule status">
                            <el-radio-group v-model="form.status">
                                <el-radio value="active" label="active">Active</el-radio>
                                <el-radio value="inactive" label="inactive">Inactive</el-radio>
                            </el-radio-group>
                        </el-form-item>
                    </el-col>
                    <el-col :span="12">
                        <el-form-item label="How many times per enquiry" required>
                            <el-input-number v-model="form.max_sends" :min="1" :max="100" controls-position="right" style="width: 100%" />
                        </el-form-item>
                    </el-col>
                </el-row>
                <el-row v-if="form.max_sends > 1" :gutter="16">
                    <el-col :span="12">
                        <el-form-item label="Next follow-up after" required>
                            <el-input-number v-model="form.repeat_amount" :min="1" :max="3650" controls-position="right" style="width: 100%" />
                        </el-form-item>
                    </el-col>
                    <el-col :span="12">
                        <el-form-item label="Unit">
                            <el-select v-model="form.repeat_unit" style="width: 100%">
                                <el-option label="Minutes" value="minutes" />
                                <el-option label="Hours" value="hours" />
                                <el-option label="Days" value="days" />
                            </el-select>
                        </el-form-item>
                    </el-col>
                </el-row>
                <p v-if="form.max_sends > 1" class="text-muted">The first message uses the timing below. Each later message waits this long after the previous one was sent, until this enquiry has received {{ form.max_sends }} messages.</p>

                <el-form-item label="Status updates" required>
                    <el-select
                        v-model="statusKeys"
                        multiple
                        filterable
                        placeholder="Choose one or more statuses the agent selects"
                        style="width: 100%"
                    >
                        <el-option-group v-for="group in statusGroups" :key="group.action_type" :label="group.label">
                            <el-option
                                v-for="status in group.statuses"
                                :key="group.action_type + status"
                                :label="group.label + ' — ' + status"
                                :value="group.action_type + '||' + status"
                            />
                        </el-option-group>
                    </el-select>
                    <div class="text-muted mt-1">The same message is sent when any of these statuses is saved.</div>
                </el-form-item>

                <h5>When to send</h5>
                <el-form-item>
                    <el-radio-group v-model="form.send_immediately">
                        <el-radio :label="true">Immediately</el-radio>
                        <el-radio :label="false">After a delay</el-radio>
                    </el-radio-group>
                </el-form-item>
                <el-row v-if="!form.send_immediately" :gutter="16">
                    <el-col :span="12">
                        <el-form-item label="Delay" required>
                            <el-input-number v-model="form.delay_amount" :min="1" :max="3650" controls-position="right" style="width: 100%" />
                        </el-form-item>
                    </el-col>
                    <el-col :span="12">
                        <el-form-item label="Unit">
                            <el-select v-model="form.delay_unit" style="width: 100%">
                                <el-option label="Minutes" value="minutes" />
                                <el-option label="Hours" value="hours" />
                                <el-option label="Days" value="days" />
                            </el-select>
                        </el-form-item>
                    </el-col>
                </el-row>

                <h5>What to send</h5>
                <el-form-item label="Message">
                    <el-radio-group v-model="form.message_mode">
                        <el-radio label="template">Template only</el-radio>
                        <el-radio label="text">Text only</el-radio>
                        <el-radio label="both">Text, then template</el-radio>
                    </el-radio-group>
                    <div class="text-muted mt-1">{{ modeHelp }}</div>
                </el-form-item>

                <div v-if="form.message_mode === 'text' || form.message_mode === 'both'">
                    <el-form-item label="WhatsApp text" required>
                        <el-input v-model="form.text_body" type="textarea" :rows="6" maxlength="4000" placeholder="Dear {{name}}, we tried calling you..." />
                        <div class="mt-2">
                            <el-button v-for="token in textTokens" :key="token.token" size="small" class="mr-2 mb-2" @click="insertToken(token.token)">
                                {{ token.label }}
                            </el-button>
                        </div>
                    </el-form-item>
                </div>

                <div v-if="form.message_mode === 'template' || form.message_mode === 'both'">
                    <el-form-item label="Approved WhatsApp template" required>
                        <el-select
                            v-model="templateKey"
                            filterable
                            placeholder="Select a template"
                            style="width: 100%"
                            @change="onTemplateChange"
                        >
                            <el-option
                                v-for="item in templates"
                                :key="item.name + item.language"
                                :label="item.label"
                                :value="item.name + '||' + item.language"
                            />
                        </el-select>
                    </el-form-item>
                    <el-alert v-if="preview || buttons.length" type="info" :closable="false" class="mb-3">
                        <pre v-if="preview" class="mb-0" style="white-space: pre-wrap">{{ previewText }}</pre>
                        <div v-if="buttons.length" class="mt-2">Buttons: {{ buttons.join(' · ') }}</div>
                    </el-alert>
                    <el-form-item v-if="requiresHeaderImage" label="Header image URL" required>
                        <el-input v-model="form.header_image_url" placeholder="https://example.com/package.jpg" />
                    </el-form-item>
                    <div v-if="form.placeholders.length">
                        <h5>Placeholder values</h5>
                        <p class="text-muted">Fixed values are the same for every customer. Enquiry fields are filled when the message is sent.</p>
                        <div
                            v-for="row in form.placeholders"
                            :key="row.key"
                            class="border rounded p-3 mb-2"
                            :class="{ 'border-danger': form.status === 'active' && !String(row.value || '').trim() }"
                        >
                            <strong>{{ row.token }}</strong>
                            <el-row :gutter="12" class="mt-2">
                                <el-col :span="8">
                                    <el-select v-model="row.source" style="width: 100%" @change="onSourceChange(row)">
                                        <el-option label="Enquiry field" value="enquiry" />
                                        <el-option label="Fixed value" value="fixed" />
                                    </el-select>
                                </el-col>
                                <el-col :span="16">
                                    <el-select v-if="row.source === 'enquiry'" v-model="row.value" placeholder="Choose a field" style="width: 100%">
                                        <el-option v-for="field in enquiryFields" :key="field.value" :label="field.label" :value="field.value" />
                                    </el-select>
                                    <el-input v-else v-model="row.value" type="textarea" :rows="2" maxlength="2000" placeholder="Value sent for this placeholder" />
                                </el-col>
                            </el-row>
                        </div>
                    </div>
                </div>
            </el-form>
            <template #footer>
                <el-button @click="dialog = false">Cancel</el-button>
                <el-button type="success" :loading="saving" @click="save">Save rule</el-button>
            </template>
        </el-dialog>
    </div>
</template>

<script>
export default {
    data() {
        return {
            tab: 'rules',
            loading: false,
            saving: false,
            savingWindow: false,
            dialog: false,
            rules: [],
            templates: [],
            templatesError: '',
            enquiryFields: [],
            statusGroups: [],
            textTokens: [],
            windowHours: 23,
            windowError: '',
            windowSaved: false,
            statusKeys: [],
            templateKey: '',
            preview: '',
            buttons: [],
            requiresHeaderImage: false,
            formError: '',
            ruleError: '',
            statusSavingId: null,
            logs: [],
            logsLoading: false,
            logsError: '',
            logTotal: 0,
            logFilters: {
                rule_id: null,
                status: null,
                page: 1,
                per_page: 25,
            },
            form: this.blankForm(),
        };
    },
    computed: {
        previewText() {
            let text = this.preview || '';
            this.form.placeholders.forEach((row) => {
                const replacement = row.source === 'enquiry'
                    ? '[' + (this.fieldLabel(row.value) || 'enquiry field') + ']'
                    : (row.value || row.token);
                text = text.split(row.token).join(replacement);
            });
            return text;
        },
        modeHelp() {
            if (this.form.message_mode === 'text') {
                return 'Sends the text only when the latest enquiry for this phone number was created inside the session window. Outside the window, the send is skipped.';
            }
            if (this.form.message_mode === 'both') {
                return 'Inside the window, send the text. If that fails, send the template. Outside the window, send the template only.';
            }
            return 'Always sends the selected template.';
        },
    },
    watch: {
        tab(value) {
            if (value === 'logs') {
                this.loadLogs(this.logFilters.page);
            }
        },
    },
    mounted() {
        this.load();
    },
    methods: {
        blankForm() {
            return {
                id: null,
                name: '',
                status: 'inactive',
                statuses: [],
                send_immediately: true,
                delay_amount: 1,
                delay_unit: 'hours',
                max_sends: 1,
                repeat_amount: 1,
                repeat_unit: 'hours',
                message_mode: 'template',
                template_name: '',
                template_language: '',
                header_image_url: '',
                placeholders: [],
                text_body: '',
            };
        },
        statusList(row) {
            if (row.statuses && row.statuses.length) {
                return row.statuses;
            }
            if (row.status_label) {
                return [{
                    action_type: row.action_type,
                    action_label: row.action_label || row.action_type,
                    status_label: row.status_label,
                }];
            }
            return [];
        },
        timingLabel(row) {
            if (row.send_immediately) {
                return 'Immediately';
            }
            return 'After ' + row.delay_amount + ' ' + row.delay_unit;
        },
        repeatLabel(row) {
            if (row.max_sends <= 1) {
                return 'Once';
            }
            if (!row.repeat_amount) {
                return 'Up to ' + row.max_sends + ' times — set the wait before the next one';
            }
            const times = row.max_sends === 1 ? 'time' : 'times';
            return 'Then every ' + row.repeat_amount + ' ' + row.repeat_unit + ', up to ' + row.max_sends + ' ' + times;
        },
        modeLabel(mode) {
            if (mode === 'text') {
                return 'Text only';
            }
            if (mode === 'both') {
                return 'Text, then template';
            }
            return 'Template only';
        },
        resultLabel(status) {
            if (status === 'pending') {
                return 'Waiting';
            }
            return status ? status.charAt(0).toUpperCase() + status.slice(1) : '';
        },
        resultType(status) {
            if (status === 'success') {
                return 'success';
            }
            if (status === 'failed') {
                return 'danger';
            }
            if (status === 'pending') {
                return 'warning';
            }
            return 'info';
        },
        channelLabel(row) {
            if (row.used_fallback) {
                return 'Template fallback';
            }
            if (row.channel === 'text') {
                return 'Text';
            }
            if (row.channel === 'template') {
                return 'Template';
            }
            return 'Not sent';
        },
        windowLabel(row) {
            if (row.window_open === true) {
                return 'Inside window';
            }
            if (row.window_open === false) {
                return 'Outside window';
            }
            return '';
        },
        fieldLabel(value) {
            const match = this.enquiryFields.find((field) => field.value === value);
            return match ? match.label : value;
        },
        load() {
            this.loading = true;
            axios.get('/marketing/status-followups/data')
                .then((response) => {
                    this.rules = response.data.rules || [];
                    this.templates = response.data.templates || [];
                    this.templatesError = response.data.templates_error || '';
                    this.enquiryFields = response.data.enquiry_fields || [];
                    this.statusGroups = response.data.status_groups || [];
                    this.textTokens = response.data.text_tokens || [];
                    this.windowHours = response.data.window_hours || 23;
                })
                .catch((error) => {
                    this.ruleError = this.errorMessage(error, 'Could not load status follow-ups.');
                })
                .finally(() => {
                    this.loading = false;
                });
        },
        loadLogs(page) {
            this.logsLoading = true;
            this.logsError = '';
            this.logFilters.page = page || 1;
            axios.get('/marketing/status-followups/logs', {
                params: {
                    rule_id: this.logFilters.rule_id || undefined,
                    status: this.logFilters.status || undefined,
                    page: this.logFilters.page,
                },
            }).then((response) => {
                this.logs = response.data.data || [];
                this.logTotal = response.data.total || 0;
            }).catch((error) => {
                this.logsError = this.errorMessage(error, 'Could not load logs.');
            }).finally(() => {
                this.logsLoading = false;
            });
        },
        saveWindow() {
            this.savingWindow = true;
            this.windowError = '';
            this.windowSaved = false;
            axios.post('/marketing/status-followups/window', {
                window_hours: this.windowHours,
            }).then((response) => {
                this.windowHours = response.data.window_hours;
                this.windowSaved = true;
            }).catch((error) => {
                this.windowError = this.errorMessage(error, 'Could not save the window.');
            }).finally(() => {
                this.savingWindow = false;
            });
        },
        openCreate() {
            this.form = this.blankForm();
            this.statusKeys = [];
            this.templateKey = '';
            this.preview = '';
            this.buttons = [];
            this.requiresHeaderImage = false;
            this.formError = '';
            this.dialog = true;
        },
        openEdit(row) {
            this.form = {
                id: row.id,
                name: row.name,
                status: row.status,
                statuses: this.statusList(row),
                send_immediately: !!row.send_immediately,
                delay_amount: row.delay_amount || 1,
                delay_unit: row.delay_unit || 'hours',
                max_sends: row.max_sends,
                repeat_amount: row.repeat_amount > 0 ? row.repeat_amount : 1,
                repeat_unit: row.repeat_unit || 'hours',
                message_mode: row.message_mode,
                template_name: row.template_name || '',
                template_language: row.template_language || '',
                header_image_url: row.header_image_url || '',
                placeholders: JSON.parse(JSON.stringify(row.placeholders || [])),
                text_body: row.text_body || '',
            };
            this.statusKeys = this.statusList(row).map((item) => item.action_type + '||' + item.status_label);
            this.templateKey = row.template_name ? row.template_name + '||' + row.template_language : '';
            this.preview = '';
            this.buttons = [];
            this.requiresHeaderImage = !!row.header_image_url;
            this.formError = '';
            this.dialog = true;
            if (this.templateKey) {
                this.loadTemplate(true);
            }
        },
        selectedStatuses() {
            return (this.statusKeys || []).map((key) => {
                const parts = String(key).split('||');
                return {
                    action_type: parts[0] || '',
                    status_label: parts.slice(1).join('||'),
                };
            }).filter((row) => row.action_type && row.status_label);
        },
        onTemplateChange() {
            this.loadTemplate(false);
        },
        loadTemplate(keepValues) {
            const keep = keepValues === true;
            const parts = (this.templateKey || '').split('||');
            if (parts.length < 2 || !parts[0]) {
                return;
            }
            this.form.template_name = parts[0];
            this.form.template_language = parts[1];
            axios.get('/marketing/status-followups/template', {
                params: { name: parts[0], language: parts[1] },
            }).then((response) => {
                this.preview = response.data.preview || '';
                this.buttons = response.data.buttons || [];
                this.requiresHeaderImage = !!response.data.requires_header_image;
                const existing = {};
                this.form.placeholders.forEach((row) => {
                    existing[row.key] = row;
                });
                this.form.placeholders = (response.data.placeholders || []).map((row) => {
                    if (keep && existing[row.key]) {
                        return {
                            key: row.key,
                            token: row.token,
                            source: existing[row.key].source,
                            value: existing[row.key].value,
                        };
                    }
                    return row;
                });
            }).catch((error) => {
                this.formError = this.errorMessage(error, 'Could not load this template.');
            });
        },
        onSourceChange(row) {
            row.value = row.source === 'enquiry' ? 'name' : '';
        },
        insertToken(token) {
            this.form.text_body = (this.form.text_body || '') + token;
        },
        save() {
            this.saving = true;
            this.formError = '';
            const payload = Object.assign({}, this.form);
            delete payload.id;
            payload.statuses = this.selectedStatuses();
            if (payload.send_immediately) {
                payload.delay_amount = 0;
            }
            const request = this.form.id
                ? axios.put('/marketing/status-followups/' + this.form.id, payload)
                : axios.post('/marketing/status-followups', payload);
            request.then(() => {
                this.dialog = false;
                this.load();
            }).catch((error) => {
                this.formError = this.errorMessage(error, 'Could not save this rule.');
            }).finally(() => {
                this.saving = false;
            });
        },
        confirmStatus(row) {
            if (this.statusSavingId !== null) {
                return false;
            }
            const next = row.status === 'active' ? 'inactive' : 'active';
            this.statusSavingId = row.id;
            this.ruleError = '';
            return axios.post('/marketing/status-followups/' + row.id + '/status', { status: next })
                .then((response) => {
                    const saved = response.data && response.data.status;
                    if (saved !== next) {
                        this.ruleError = 'The rule status did not change. Save the rule again.';
                        return false;
                    }
                    this.load();
                    return true;
                })
                .catch((error) => {
                    this.ruleError = this.errorMessage(error, 'Could not change the rule status.');
                    if (next === 'active' && error.response && error.response.status === 422) {
                        this.openEdit(row);
                        this.form.status = 'active';
                        this.formError = this.ruleError;
                    }
                    return false;
                })
                .finally(() => {
                    this.statusSavingId = null;
                });
        },
        errorMessage(error, fallback) {
            const data = error.response && error.response.data ? error.response.data : {};
            if (data.errors) {
                const first = Object.keys(data.errors)[0];
                if (first && data.errors[first] && data.errors[first][0]) {
                    return data.errors[first][0];
                }
            }
            return data.message || fallback;
        },
    },
};
</script>
